import { useApolloClient } from '@apollo/client';
import { useSelector } from 'react-redux';
import { bugsnag } from 'lib/external/bugsnag';

import { APPEND_WOO_CUSTOM_FIELDS_IF_VERSION } from 'lib/api/mutations';
import { GET_WORK_ORDER_OPERATION_CUSTOM_FIELDS } from 'lib/api/queries';
import { getBugsnagUserInfo } from 'lib/selectors/getBugsnagUserInfo';
import {
  PL_CONSUMPTIONS_KEY,
  LP_NUMBER_KEY,
  CONSUMPTION_QTY_KEY,
  ITEM_NUMBER_KEY,
  PICK_ID_KEY,
  LOCATION_KEY,
} from 'lib/selectors/getPickingFieldConfig';
import {
  PL_CONSUMPTIONS_VERSION_KEY,
  nextVersion,
  readVersion,
  versionWhere,
} from 'lib/utils/versionedCustomFields';

const MAX_WRITE_ATTEMPTS = 3;

/**
 * Submits picked license plates by appending them to the work order operation's
 * customFields[PL_CONSUMPTIONS_KEY] array. The dynamics365-connector reads pending
 * entries (those without plJournalId) off the WOO, posts the
 * ProductionPickingListJournalEntries to D365, then stamps plJournalId back onto
 * each entry.
 *
 * Writes only plConsumptions (jsonb _append), so other keys (availableMaterials, and any other
 * writer's keys) are never overwritten, and only if
 * plConsumptionsVersion is still the value just read. If another writer got
 * there first it re-reads and reapplies (up to MAX_WRITE_ATTEMPTS). Picks whose
 * pickId is already on the WOO are skipped, so a retry after a write that did
 * land never adds them twice.
 *
 * A failed request may still have written (the response was lost), so before
 * rejecting it reads the WOO again: if every pick is there, the submit succeeded.
 *
 * Returns an async submit fn that resolves to { alreadyRecorded }: the picks that
 * were skipped because their pickId was already recorded with a different
 * quantity, as { licensePlate, quantity } with the recorded quantity. It rejects
 * on failure so callers leave the picks unsubmitted and surface an error.
 *
 * @returns {(wooRef: (string|number), picks: Array) => Promise<{alreadyRecorded: Array}>}
 */
const useSubmitPicks = () => {
  const client = useApolloClient();
  const bugsnagUserInfo = useSelector(getBugsnagUserInfo);

  const report = (err) => {
    bugsnag.notify(err, (event) => {
      event.addMetadata('user', bugsnagUserInfo);
    });
  };

  const readCustomFields = async (wooRef) => {
    const { data } = await client.query({
      query: GET_WORK_ORDER_OPERATION_CUSTOM_FIELDS,
      variables: { workOrderOperationRef: wooRef },
      fetchPolicy: 'network-only',
    });
    return data?.erpWorkOrderOperation?.customFields || {};
  };

  const consumptionsOf = (customFields) => {
    return Array.isArray(customFields[PL_CONSUMPTIONS_KEY])
      ? customFields[PL_CONSUMPTIONS_KEY]
      : [];
  };

  const submitPicks = async (wooRef, picks) => {
    // itemNumber/pickId/location make the entry self-describing so the picking
    // UI can rebuild pick rows from this durable record after a page reload
    // (usePickingSummary) — itemNumber groups the row under its material even
    // after the LP drops out of availableMaterials, and pickId lets in-memory
    // Redux picks dedupe exactly against hydrated entries. The connector only
    // reads LP + qty and passes the rest through.
    const newEntries = picks.map((pick) => {
      return {
        [LP_NUMBER_KEY]: pick.licensePlate,
        [CONSUMPTION_QTY_KEY]: pick.quantity,
        [ITEM_NUMBER_KEY]: pick.itemNumber,
        [PICK_ID_KEY]: pick.id,
        ...(pick.location ? { [LOCATION_KEY]: pick.location } : {}),
      };
    });

    const recordedById = (existing) => {
      return new Map(
        existing.map((entry) => {
          return [entry[PICK_ID_KEY], entry];
        })
      );
    };
    const alreadyRecordedIn = (byId) => {
      return newEntries
        .filter((entry) => {
          const recorded = byId.get(entry[PICK_ID_KEY]);
          return (
            recorded &&
            recorded[CONSUMPTION_QTY_KEY] !== entry[CONSUMPTION_QTY_KEY]
          );
        })
        .map((entry) => {
          return {
            licensePlate: entry[LP_NUMBER_KEY],
            quantity: byId.get(entry[PICK_ID_KEY])[CONSUMPTION_QTY_KEY],
          };
        });
    };

    try {
      for (let attempt = 1; attempt <= MAX_WRITE_ATTEMPTS; attempt += 1) {
        // Fresh, uncached read so we merge against the latest writeback.
        // eslint-disable-next-line no-await-in-loop
        const customFields = await readCustomFields(wooRef);
        const existing = consumptionsOf(customFields);
        const byId = recordedById(existing);
        const toAdd = newEntries.filter((entry) => {
          return !byId.has(entry[PICK_ID_KEY]);
        });
        const alreadyRecorded = alreadyRecordedIn(byId);
        if (!toAdd.length) return { alreadyRecorded };

        const version = readVersion(customFields[PL_CONSUMPTIONS_VERSION_KEY]);
        // eslint-disable-next-line no-await-in-loop
        const { data, errors } = await client.mutate({
          mutation: APPEND_WOO_CUSTOM_FIELDS_IF_VERSION,
          variables: {
            where: versionWhere(wooRef, PL_CONSUMPTIONS_VERSION_KEY, version),
            fields: {
              [PL_CONSUMPTIONS_KEY]: [...existing, ...toAdd],
              [PL_CONSUMPTIONS_VERSION_KEY]: nextVersion(version),
            },
          },
        });
        if (errors?.length) throw new Error('Failed to submit picks');
        if (data?.updateErpWorkOrderOperations?.affected_rows > 0) {
          // Refresh the cached customFields the picking screens read.
          readCustomFields(wooRef).catch(() => {});
          return { alreadyRecorded };
        }
      }
      throw new Error('Failed to submit picks: the work order kept changing');
    } catch (err) {
      // The write may have landed with its response lost: if every pick is on
      // the WOO now, the submit succeeded.
      try {
        const byId = recordedById(
          consumptionsOf(await readCustomFields(wooRef))
        );
        const allRecorded = newEntries.every((entry) => {
          return byId.has(entry[PICK_ID_KEY]);
        });
        if (allRecorded) return { alreadyRecorded: alreadyRecordedIn(byId) };
      } catch {
        // Could not confirm; report the original failure.
      }
      report(err);
      throw err;
    }
  };

  return submitPicks;
};

export default useSubmitPicks;
